<!--
  @component NewProjectWizard — the consumer "+ New project" flow.
  tier:      wizard (composes the Wizard primitive)
  reads:     useIdentityIO     (createProject + projects list)
             useDeploymentIO   (dispatch projects.upsert + setDeployImage)
  effects:   1. identity.createProject(name)   — local topology target
             2. deployment.dispatch("projects.upsert", { id, name,
                                       deployImage, region })  — D2.5
             3. push("/projects/{id}/overview")

  D2.5 grows the wizard from 2 → 3 steps to capture a deployImage
  + region at create-time. With the deployment server wired (D2/D3
  shipped), the wizard now has something honest to provision:
  the server persists the project + deployImage so the Deploy button
  on the deployments panel can run release.run end-to-end.

  Steps:
    Step 0 — Name        (1..40 lowercase + dashes, unique)
    Step 1 — Deploy      (deployImage + region; deferrable)
    Step 2 — Review

  Future steps (DON'T add yet — wait for the connector wires):
    - Substrate vendor matrix  (post-m..r connector wiring)
    - DB preset                (post-d-arc database adapter)
    - Domain hint              (post-l-arc DNS adapter)
-->
<script lang="ts">
  import { push } from "svelte-spa-router";
  import Wizard, {
    type WizardStepDef,
  } from "../../../global/visuals/primitives/Wizard.svelte";
  import { useIdentityIO } from "../accounts/IdentityIO.svelte";
  import { useDeploymentIO } from "../../../global/io/nodes/deployment/DeploymentIO.svelte";
  import { useValidationIO } from "../../../global/io/nodes/security/ValidationIO.svelte";

  interface Props {
    open: boolean;
    onClose: () => void;
  }
  const { open, onClose }: Props = $props();

  const identity = useIdentityIO();
  const deployment = useDeploymentIO();
  const validation = useValidationIO();

  // ─── Step plumbing ─────────────────────────────────────────────
  const STEPS: readonly WizardStepDef[] = [
    { id: "name",   label: "Name" },
    { id: "deploy", label: "Deploy" },
    { id: "review", label: "Review" },
  ];

  /** Default OCI image — picked because:
   *  - already public on Docker Hub (no registry auth needed)
   *  - listens on port 80 (matches our default services config)
   *  - serves a recognisable welcome page so "the deploy worked"
   *    is visible at first glance.
   *  Replace via the Deploy step input. */
  const DEFAULT_IMAGE = "docker.io/caddy:2-alpine";
  /** Default Fly region (Newark). Region picker arrives with the
   *  c-arc topology adapter; until then a single sane default. */
  const DEFAULT_REGION = "ewr";

  let step = $state(0);
  let name = $state("");
  let deployImage = $state<string>(DEFAULT_IMAGE);
  let region = $state<string>(DEFAULT_REGION);
  let pending = $state(false);
  let error: string | null = $state(null);

  // Reset state every time the wizard opens (so re-opening after
  // cancel gives a clean form).
  $effect(() => {
    if (open) {
      step = 0;
      name = "";
      deployImage = DEFAULT_IMAGE;
      region = DEFAULT_REGION;
      pending = false;
      error = null;
    }
  });

  // ─── Validation ────────────────────────────────────────────────
  const NAME_RE = /^[a-z0-9](?:[a-z0-9-]{0,38}[a-z0-9])?$/;

  const trimmed = $derived(name.trim().toLowerCase());

  const nameError = $derived.by<string | null>(() => {
    if (!trimmed) return null; // empty: don't show error yet
    if (trimmed.length < 1 || trimmed.length > 40) {
      return "must be 1–40 characters";
    }
    if (!NAME_RE.test(trimmed)) {
      return "lowercase a–z, 0–9, and dashes (no leading/trailing)";
    }
    if (
      identity.projects.some(
        (p) => p.name.trim().toLowerCase() === trimmed,
      )
    ) {
      return "already used by another project";
    }
    return null;
  });

  // Light validation: OCI image refs are notoriously loose; we only
  // assert non-empty + no whitespace. The server (and Fly) will
  // surface a structured error if the pull fails.
  const trimmedImage = $derived(deployImage.trim());
  const imageError = $derived.by<string | null>(() => {
    if (!trimmedImage) return null;
    if (/\s/.test(trimmedImage)) return "image refs can't contain whitespace";
    return null;
  });

  // Client-side sanitization PRE-CHECK (UX defense-in-depth; the
  // deployment server re-validates authoritatively and is the sole
  // authority). We surface a subtle warning when the input would be
  // sanitized but never hard-block — the server decides.
  const nameCheck = $derived(validation.check(name, "html"));
  const imageCheck = $derived(validation.check(deployImage, "path"));

  const canAdvance = $derived.by(() => {
    if (pending) return false;
    if (step === 0) return trimmed.length > 0 && nameError === null;
    if (step === 1) return trimmedImage.length > 0 && imageError === null;
    return true;
  });

  // ─── Finish ────────────────────────────────────────────────────
  async function finish(): Promise<void> {
    if (!canAdvance || pending) return;
    pending = true;
    error = null;
    try {
      // 1. Local topology target (gives us the project.id we use as
      //    targetId for the server's projects.upsert).
      const project = await identity.createProject(trimmed);

      // 2. Server-side projects.upsert — persists deployImage + region
      //    so the Deploy button on the deployments panel can resolve
      //    them and dispatch release.run end-to-end. Dispatched
      //    through the deployment node's command port (NetworkDeployment
      //    hyperedge owns the WebSocketCommandPort).
      try {
        await deployment.dispatch("projects.upsert", {
          id: project.id,
          name: project.name,
          deployImage: trimmedImage,
          region: region.trim() || DEFAULT_REGION,
        });
      } catch (e) {
        // Don't lose the local project on a server error — the user
        // can retry the upsert from Settings → Deploy image. Surface
        // a soft warning rather than failing the whole flow.
        const msg = e instanceof Error ? e.message : String(e);
        console.warn("projects.upsert failed (project created locally only):", msg);
      }

      // 3. Close the wizard, then navigate. Land on /deployments
      //    (not /overview) because the user's next action after
      //    creating a project is overwhelmingly "deploy it" — putting
      //    them on the Deploy panel saves a click in the onboarding flow.
      onClose();
      push(`/projects/${project.id}/deployments`);
    } catch (e) {
      error = e instanceof Error ? e.message : "could not create project";
    } finally {
      pending = false;
    }
  }
</script>

<Wizard
  {open}
  title="New project"
  steps={STEPS}
  currentStep={step}
  canAdvance={canAdvance}
  pending={pending}
  finishLabel="Create project"
  onStepChange={(next) => (step = next)}
  onFinish={() => void finish()}
  onCancel={onClose}
>
  {#snippet children(stepIndex: number)}
    {#if stepIndex === 0}
      <div class="step">
        <label>
          <span>project name</span>
          <input
            type="text"
            placeholder="my-app"
            autocomplete="off"
            spellcheck="false"
            bind:value={name}
            disabled={pending}
            maxlength={40}
          />
        </label>
        <p class="hint" class:bad={!!nameError}>
          {nameError ?? "lowercase letters, numbers, and dashes. doubles as your project's topology target id prefix."}
        </p>
        {#if name && !nameCheck.ok}
          <p class="warn">contains characters that will be sanitized · stored as <code>{nameCheck.sanitized}</code></p>
        {/if}
      </div>
    {:else if stepIndex === 1}
      <div class="step">
        <label>
          <span>deploy image</span>
          <input
            type="text"
            placeholder={DEFAULT_IMAGE}
            autocomplete="off"
            spellcheck="false"
            bind:value={deployImage}
            disabled={pending}
          />
        </label>
        <p class="hint" class:bad={!!imageError}>
          {imageError ?? "OCI image reference. Defaults to caddy:2-alpine — a public image that serves on port 80. Override once you've pushed your own. Editable later in Settings."}
        </p>
        {#if deployImage && !imageCheck.ok}
          <p class="warn">image ref will be sanitized server-side → <code>{imageCheck.sanitized}</code></p>
        {/if}

        <label>
          <span>region</span>
          <input
            type="text"
            placeholder={DEFAULT_REGION}
            autocomplete="off"
            spellcheck="false"
            bind:value={region}
            disabled={pending}
            maxlength={4}
          />
        </label>
        <p class="hint">
          Fly region code. <code>ewr</code> = Newark. The full picker
          arrives with the topology adapter; for now any valid Fly
          region works.
        </p>
      </div>
    {:else}
      <div class="step review">
        <dl>
          <div class="row">
            <dt>name</dt>
            <dd><code>{trimmed}</code></dd>
          </div>
          <div class="row">
            <dt>owner</dt>
            <dd><code>{identity.user?.email ?? "—"}</code></dd>
          </div>
          <div class="row">
            <dt>deploy image</dt>
            <dd><code>{trimmedImage}</code></dd>
          </div>
          <div class="row">
            <dt>region</dt>
            <dd><code>{region.trim() || DEFAULT_REGION}</code></dd>
          </div>
          <div class="row">
            <dt>topology target id</dt>
            <dd><code class="muted">proj-… (generated on create)</code></dd>
          </div>
        </dl>

        <p class="note">
          <strong>Next:</strong> we'll create the project +
          register it with the deployment server, then land you on
          the <em>Deployments</em> panel. One click there runs the
          full release pipeline (preview → gates → provisioning →
          cutover → drain → finalize) against Fly.
        </p>

        {#if error}
          <p class="error">{error}</p>
        {/if}
      </div>
    {/if}
  {/snippet}
</Wizard>

<style>
  .step {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }
  label {
    display: flex;
    flex-direction: column;
    gap: var(--space-1, 4px);
  }
  label span {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--text-tertiary);
  }
  input {
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-sm);
    border: 0.5px solid var(--border-input);
    background: var(--surface-sunken);
    color: var(--text-primary);
    font: inherit;
    font-size: 13px;
    font-family: var(--type-mono);
  }
  input:focus {
    outline: none;
    border-color: var(--border-focus);
  }
  .hint {
    margin: 0;
    font-size: 11px;
    color: var(--text-tertiary);
    line-height: 1.45;
  }
  .hint.bad {
    color: var(--error);
    font-family: var(--type-mono);
  }
  .warn {
    margin: 0;
    font-size: 11px;
    color: var(--error);
    line-height: 1.45;
  }
  .warn code {
    font-family: var(--type-mono);
    word-break: break-all;
  }
  .review dl {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    margin: 0;
  }
  .row {
    display: flex;
    gap: var(--space-3);
    align-items: baseline;
  }
  dt {
    flex: 0 0 140px;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--text-tertiary);
  }
  dd {
    margin: 0;
    font-size: 12px;
    color: var(--text-primary);
  }
  dd code {
    font-family: var(--type-mono);
    font-size: 12px;
  }
  dd code.muted {
    color: var(--text-tertiary);
  }
  .note {
    margin: var(--space-2) 0 0;
    padding: var(--space-3);
    background: var(--warning-subtle);
    border-radius: var(--radius-sm);
    font-size: 12px;
    color: var(--text-secondary);
    line-height: 1.55;
  }
  .note strong {
    color: var(--warning);
    font-weight: 500;
  }
  .error {
    margin: 0;
    font-size: 12px;
    color: var(--error);
    font-family: var(--type-mono);
  }
</style>
